import { IrdPropertyType } from '../property';
import { IrdFormat, IrdRenderMode } from './resolved';

/**
 * Formats that read the same in every feature. A feature declares one of these rather than its
 * own copy, so "Yes" is translated once, not once per entity.
 */

/**
 * Renders a boolean as `<prefix>.TRUE` or `<prefix>.FALSE`, resolved in the active language.
 *
 * Use one of the shared pairs below; pass a feature's own prefix only when the pair needs wording
 * of its own ("Verified" / "Unverified"), with `TRUE` and `FALSE` beneath it.
 */
export const asBoolean = (prefix: string): IrdFormat<boolean> => ({
  kind: 'translation-key',
  render: (value) => `${prefix}.${value ? 'TRUE' : 'FALSE'}`,
});

export const AS_YES_NO = asBoolean('COMMON.BOOLEAN.YES_NO');
export const AS_ON_OFF = asBoolean('COMMON.BOOLEAN.ON_OFF');
export const AS_ENABLED_DISABLED = asBoolean('COMMON.BOOLEAN.ENABLED_DISABLED');

/** Joins a collection for display, in the order the service sent it. */
export const AS_LIST: IrdFormat<readonly unknown[]> = { kind: 'list', separator: ', ' };

/** Renders a string map as `key: value` pairs. */
export const AS_ENTRIES: IrdFormat<Readonly<Record<string, string>>> = {
  kind: 'custom',
  render: (value) =>
    Object.entries(value)
      .map(([key, entry]) => `${key}: ${entry}`)
      .join(', '),
};

/** Renders any structure as its JSON text. */
export const AS_JSON: IrdFormat = { kind: 'custom', render: (value) => JSON.stringify(value) };

const AS_PLAIN_TEXT: IrdRenderMode = { kind: 'text' };

/**
 * How a property renders when it declares no `renderAs`. Keyed by the whole type union, so a new
 * type does not compile until its default is decided.
 *
 * Only a property that declares no `format` either reads these: a declared format is the string the
 * author chose, and markup around it would override that choice.
 */
export const IRD_RENDER_MODE_DEFAULTS: Readonly<Record<IrdPropertyType, IrdRenderMode>> = {
  string: AS_PLAIN_TEXT,
  number: AS_PLAIN_TEXT,
  boolean: AS_PLAIN_TEXT,
  Date: AS_PLAIN_TEXT,
  Object: AS_PLAIN_TEXT,
  select: AS_PLAIN_TEXT,
  enum: AS_PLAIN_TEXT,
  icon: { kind: 'icon' },
  color: { kind: 'color' },
  json: { kind: 'json' },
};

/** The declared mode, else plain text under a declared format, else the type's default. */
export function resolveRenderMode(
  type: IrdPropertyType,
  format: IrdFormat | undefined,
  renderAs: IrdRenderMode | undefined,
): IrdRenderMode {
  if (renderAs) {
    return renderAs;
  }
  return format ? AS_PLAIN_TEXT : IRD_RENDER_MODE_DEFAULTS[type];
}
